import { MouseEvent, Suspense, useContext, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import { Tag } from '@givebutter/primitives/tag';
import { maskDecimal } from '@givebutter/util';
import {
    faArchive,
    faEllipsisVertical,
    faFileExport,
    faFileImport,
    faHashtag,
    faInputText,
    faMerge,
    faTag,
    faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCustomFields } from 'api/customFields';
import Content from 'assets/js/components/Content';
import * as ExportDataDialog from 'assets/js/components/ExportDataDialog';
import * as ExportDialog from 'assets/js/components/ExportDialog';
import * as ContactLogEmail from 'assets/js/pages/contacts/ContactLogEmail';
import { ContactTypeContext } from 'assets/js/pages/contacts/ContactTypeContext';
import { ContactsHeader } from 'assets/js/pages/contacts/ContactsHeader';
import { ContactsTable } from 'assets/js/pages/contacts/ContactsTable';
import { ContactsTableProvider } from 'assets/js/pages/contacts/ContactsTableProvider';
import { ArchivedChoices, ContactsViewContext } from 'assets/js/pages/contacts/ContactsViewContext';
import { NewContactButton } from 'assets/js/pages/contacts/NewContactButton';
import { BulkActionsNotice } from 'assets/js/pages/contacts/bulk-actions';
import {
    configureCustomFieldColumn,
    getCustomFieldColumnKey,
} from 'assets/js/pages/contacts/customFieldColumn';
import {
    ContactsModal,
    useContactsModalState,
} from 'assets/js/pages/contacts/hooks/useContactsModalState';
import { useExportContacts } from 'assets/js/pages/contacts/hooks/useExportContacts';
import { curriedTransformTableKeyToReadable } from 'assets/js/pages/contacts/transform';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Import from 'components/imports/Import';
import { DownloadImportTemplateModal } from 'components/pages/contacts/components/DownloadImportTemplateModal';
import * as ExportEOYReportAlert from 'components/pages/contacts/components/ExportEOYReportAlert';
import * as ExportReportsHistoryAlert from 'components/pages/contacts/components/ExportReportsHistoryAlert';
import * as ExportSuccessAlert from 'components/pages/contacts/components/ExportSuccessAlert';
import { OveragesWarning } from 'components/pages/contacts/components/OveragesWarning';
import * as SubNav from 'components/sub-nav';
import { ContactsSubNavTabs } from 'components/sub-nav/ContactsSubNavTabs';
import * as Table from 'components/table';
import { TableContext } from 'components/table/TableContext';
import { TableCoreResourceProvider } from 'components/table/TableCoreResourceContext';
import * as Tooltip from 'components/tooltip';
import { EmptyData } from 'components/util';
import { ContactType } from 'constants/contacts';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { customFieldQueryKeys } from 'queries/custom-field-query-keys';
import { Address } from 'types/domain';

const TAG_SLICE = 3;

const Contacts = () => {
    const { newExportModal, contactBulkActions } = useFlags();
    const history = useHistory();
    const { state: locationState } = useLocation<{ openExportSummariesDialog?: boolean }>();
    const { accountId } = useCurrentAccount();
    const { table } = useContext(TableContext);
    const { archivedInput, setIsArchived: setArchivedInput } = useContext(ContactsViewContext);
    const { openModal, setOpenModal, setExportReportType, exportReportType } =
        useContactsModalState(
            locationState?.openExportSummariesDialog ? ContactsModal.ExportEOYReports : undefined,
        );
    const { exportContacts } = useExportContacts();

    return (
        <ContactTypeContext.Provider value={ContactType.Individual}>
            <div className={'flex flex-col gap-y-4'}>
                <SubNav.Root>
                    {contactBulkActions && (
                        <Suspense fallback={null}>
                            <BulkActionsNotice />
                        </Suspense>
                    )}
                    <SubNav.Header>
                        <SubNav.Title>
                            <div className={'flex items-center gap-x-4'}>
                                Contacts
                                <ContactsHeader />
                            </div>
                        </SubNav.Title>
                        <SubNav.Actions>
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild={true}>
                                    <Button variant="outline" color="neutral">
                                        <FontAwesomeIcon icon={faFileExport} />
                                        Export individuals
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content sideOffset={5} align={'end'}>
                                    {newExportModal ? (
                                        <Suspense
                                            fallback={
                                                <DropdownMenu.Item className="flex gap-2" disabled>
                                                    <FontAwesomeIcon icon={faFileExport} />
                                                    Export to CSV
                                                </DropdownMenu.Item>
                                            }
                                        >
                                            <ExportDialog.Root
                                                reportType="contacts"
                                                params={{
                                                    archived: archivedInput === 'archived',
                                                }}
                                            >
                                                <ExportDialog.Trigger asChild={true}>
                                                    <DropdownMenu.Item
                                                        onSelect={e => e.preventDefault()}
                                                        className="flex gap-2"
                                                    >
                                                        <FontAwesomeIcon icon={faFileExport} />
                                                        Export to CSV
                                                    </DropdownMenu.Item>
                                                </ExportDialog.Trigger>
                                            </ExportDialog.Root>
                                        </Suspense>
                                    ) : (
                                        <ExportDataDialog.Root
                                            label="Contacts"
                                            onSubmit={exportContacts}
                                        >
                                            <ExportDataDialog.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                    className="flex gap-2"
                                                >
                                                    <FontAwesomeIcon icon={faFileExport} />
                                                    Export to CSV
                                                </DropdownMenu.Item>
                                            </ExportDataDialog.Trigger>
                                        </ExportDataDialog.Root>
                                    )}
                                    <DropdownMenu.Item
                                        onClick={() => setOpenModal(ContactsModal.ExportEOYReports)}
                                    >
                                        Send Year-End Summaries
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item
                                        onClick={() => setOpenModal(ContactsModal.ExportsHistory)}
                                    >
                                        Year-End Summary History
                                    </DropdownMenu.Item>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild={true}>
                                    <Button variant="outline" color="neutral">
                                        <FontAwesomeIcon icon={faFileImport} />
                                        Import
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content sideOffset={5} align={'end'}>
                                    <Suspense
                                        fallback={
                                            <DropdownMenu.Item disabled={true}>
                                                <FontAwesomeIcon icon={faFileImport} /> Contact
                                                import
                                            </DropdownMenu.Item>
                                        }
                                    >
                                        <Import.Root strategy="contacts">
                                            <Import.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onClick={e => e.preventDefault()}
                                                    className="grid grid-cols-[16px_1fr] items-center gap-2"
                                                >
                                                    <FontAwesomeIcon icon={faFileImport} />
                                                    <span>Contact import</span>
                                                </DropdownMenu.Item>
                                            </Import.Trigger>
                                            <Import.Portal>
                                                <Import.Space />
                                            </Import.Portal>
                                        </Import.Root>
                                    </Suspense>
                                    <DropdownMenu.Item
                                        onClick={() => setOpenModal(ContactsModal.ImportTemplate)}
                                    >
                                        Download import template
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item
                                        onClick={() =>
                                            history.push(`/accounts/${accountId}/settings/imports`)
                                        }
                                    >
                                        View import history
                                    </DropdownMenu.Item>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                            <NewContactButton />
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild={true}>
                                    <Button variant="outline" color="neutral" shape={'square'}>
                                        <span className="sr-only">Actions</span>
                                        <FontAwesomeIcon icon={faEllipsisVertical} />
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content sideOffset={5} align={'end'}>
                                    <ContactLogEmail.Root>
                                        <ContactLogEmail.Trigger asChild={true}>
                                            <DropdownMenu.Item
                                                onSelect={e => e.preventDefault()}
                                                className="grid grid-cols-[16px_1fr] items-center gap-2"
                                            >
                                                <FontAwesomeIcon icon={faMerge} /> Log email
                                            </DropdownMenu.Item>
                                        </ContactLogEmail.Trigger>
                                    </ContactLogEmail.Root>
                                    <DropdownMenu.Item
                                        className="grid grid-cols-[16px_1fr] items-center gap-2"
                                        onClick={() =>
                                            history.push(
                                                `/accounts/${accountId}/contacts/custom-fields`,
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon icon={faInputText} /> Custom fields
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item
                                        className="grid grid-cols-[16px_1fr] items-center gap-2"
                                        onClick={() =>
                                            history.push(`/accounts/${accountId}/contacts/tags`)
                                        }
                                    >
                                        <FontAwesomeIcon icon={faTag} /> Manage tags
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Separator />
                                    <DropdownMenu.RadioGroup
                                        value={archivedInput}
                                        onValueChange={value => {
                                            table.toggleAllRowsSelected?.(false);
                                            setArchivedInput(value as ArchivedChoices);
                                        }}
                                    >
                                        <DropdownMenu.RadioItem value={'active'}>
                                            <span className="grid grid-cols-[16px_1fr] items-center gap-2">
                                                <FontAwesomeIcon icon={faUser} /> Active
                                            </span>
                                        </DropdownMenu.RadioItem>
                                        <DropdownMenu.RadioItem value={'archived'}>
                                            <span className="grid grid-cols-[16px_1fr] items-center gap-2">
                                                <FontAwesomeIcon icon={faArchive} /> Archived
                                            </span>
                                        </DropdownMenu.RadioItem>
                                    </DropdownMenu.RadioGroup>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </SubNav.Actions>
                    </SubNav.Header>
                    <ContactsSubNavTabs />
                </SubNav.Root>
                <ContactsTable>
                    <div className="mt-5 flex flex-wrap justify-center gap-3 sm:flex-nowrap">
                        <Import.Root strategy="contacts">
                            <Import.Trigger asChild={true}>
                                <Button
                                    className="flex items-center gap-1"
                                    variant={'outline'}
                                    color={'neutral'}
                                >
                                    <FontAwesomeIcon icon={faFileImport} />
                                    Import contacts
                                </Button>
                            </Import.Trigger>
                            <Import.Space />
                        </Import.Root>
                        <NewContactButton />
                    </div>
                </ContactsTable>
                <ExportEOYReportAlert.Root
                    resource="contacts"
                    template="contact.summary"
                    onSuccess={reportType => {
                        setExportReportType(reportType);
                        setOpenModal(ContactsModal.ExportSuccess);
                    }}
                    open={openModal === ContactsModal.ExportEOYReports}
                    onClose={() => setOpenModal(ContactsModal.None)}
                />
                <ExportReportsHistoryAlert.Root
                    open={openModal === ContactsModal.ExportsHistory}
                    onClose={() => setOpenModal(ContactsModal.None)}
                />
                <ExportSuccessAlert.Root
                    open={openModal === ContactsModal.ExportSuccess}
                    exportType={exportReportType}
                    onClose={() => setOpenModal(ContactsModal.None)}
                />
                <DownloadImportTemplateModal
                    type={'contacts'}
                    open={openModal === ContactsModal.ImportTemplate}
                    onOpenChange={() => setOpenModal(ContactsModal.None)}
                />
            </div>
        </ContactTypeContext.Provider>
    );
};

const ContactsWrapper = () => {
    const { transactionSoftCredits } = useFlags();
    const { accountId } = useCurrentAccount();
    const [archivedInput, setArchivedInput] = useState<ArchivedChoices>('active');
    const isArchived = archivedInput === 'archived';

    const { data: customFields } = useSuspenseQuery({
        queryKey: customFieldQueryKeys.customFieldsWithFilters(accountId, {
            group: 'contact',
            limit: 999,
        }),
        queryFn: () => fetchCustomFields(accountId, { group: 'contact', limit: 999 }),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });
    const contactCustomFields = useMemo(() => customFields.data ?? [], [customFields.data]);
    const customFieldColumnInitialState = contactCustomFields.reduce(
        (prev, curr) => ({
            ...prev,
            [getCustomFieldColumnKey(curr)]: false,
        }),
        {},
    );

    const ctx = useMemo(
        () => ({
            archivedInput,
            setIsArchived: setArchivedInput,
        }),
        [archivedInput],
    );

    return (
        <TableCoreResourceProvider
            resource={'contacts'}
            extraParams={{
                isArchived,
            }}
            initialState={{
                sortingState: [{ id: 'created_at', desc: true }],
                columnVisibilityState: {
                    [Table.Constants.Select]: true,
                    first_name: true,
                    last_name: true,
                    total_contributions: true,
                    primary_email: true,
                    created_at: true,
                    tags: false,
                    notifications: false,
                    stats: false,
                    primary_phone: false,
                    primary_address_formatted: false,
                    address_1: false,
                    address_2: false,
                    city: false,
                    state: false,
                    zip: false,
                    country: false,
                    gender: false,
                    dob: false,
                    company: false,
                    employer: false,
                    associated_companies: false,
                    title: false,
                    note: false,
                    website_url: false,
                    facebook_url: false,
                    linkedin_url: false,
                    total_soft_credits: false,
                    x_url: false,
                    additional_emails: false,
                    additional_phones: false,
                    additional_addresses: false,
                    ...customFieldColumnInitialState,
                },
                columnOrderState: [
                    'first_name',
                    'last_name',
                    'primary_email',
                    'created_at',
                    'total_contributions',
                ],
                columnPinningState: { left: [Table.Constants.Select] },
            }}
            columns={columnHelper =>
                [
                    columnHelper.display({
                        id: Table.Constants.Select,
                        header: () => <Table.HeaderCheckbox />,
                        cell: () => <Table.RowCheckbox />,
                        meta: {
                            ignoreLink: true,
                        },
                    }),
                    columnHelper.accessor('first_name', {
                        header: curriedTransformTableKeyToReadable,
                        enableHiding: true,
                        cell: () => <Table.CellDataOrEmpty className={'font-medium'} />,
                    }),
                    columnHelper.accessor('last_name', {
                        header: curriedTransformTableKeyToReadable,
                        enableHiding: true,
                        cell: () => <Table.CellDataOrEmpty className={'font-medium'} />,
                    }),
                    columnHelper.accessor('stats', {
                        id: 'total_contributions',
                        header: curriedTransformTableKeyToReadable,
                        cell: info => {
                            const contributions = info.getValue().contributions ?? 0;
                            return (
                                <>
                                    <CurrencySymbol />
                                    {maskDecimal(contributions / 100)}
                                </>
                            );
                        },
                        meta: {
                            sortType: 'numeric',
                        },
                    }),
                    columnHelper.accessor('primary_email', {
                        header: curriedTransformTableKeyToReadable,
                        enableHiding: true,
                        cell: info => {
                            const value = info.getValue();

                            if (value === 'N/A') {
                                return <EmptyData />;
                            }

                            return value;
                        },
                    }),
                    columnHelper.accessor('created_at', {
                        header: curriedTransformTableKeyToReadable,
                        cell: info =>
                            DateTime.fromISO(info.getValue()).toLocaleString(DateTime.DATE_FULL),
                        meta: {
                            sortType: 'date',
                        },
                    }),
                    columnHelper.accessor('primary_phone', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: info => {
                            const value = info.getValue();

                            return (
                                <p className="whitespace-nowrap">
                                    {value ? getFormattedPhoneNumber(value) : <EmptyData />}
                                </p>
                            );
                        },
                    }),
                    columnHelper.accessor('primary_address_formatted', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('address_1', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('address_2', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('city', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('state', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('zip', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('country', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('gender', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('dob', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('employer', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('associated_companies', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: info => {
                            const companies = info.row.original.associated_companies;

                            if (!companies?.length) {
                                return <EmptyData />;
                            }

                            const maxDisplayed = 2;
                            const displayedCompanies = companies.slice(0, maxDisplayed);
                            const hiddenCompanies = companies.slice(maxDisplayed);

                            return (
                                <span>
                                    {displayedCompanies.map((company, index) => (
                                        <span key={company.id}>
                                            {index > 0 && ', '}
                                            <Link
                                                to={`/accounts/${accountId}/companies/${company.id}`}
                                            >
                                                {company.company_name}
                                            </Link>
                                        </span>
                                    ))}
                                    {hiddenCompanies.length > 0 && (
                                        <span>
                                            {`, `}
                                            <Tooltip.Root>
                                                <Tooltip.Trigger>
                                                    <span>and {hiddenCompanies.length} more</span>
                                                </Tooltip.Trigger>
                                                <Tooltip.Portal>
                                                    <Tooltip.Content inverse>
                                                        <Tooltip.Arrow inverse />
                                                        <div className="flex flex-col">
                                                            {hiddenCompanies.map(company => (
                                                                <Link
                                                                    key={company.id}
                                                                    to={`/accounts/${accountId}/companies/${company.id}`}
                                                                    className="hover:text-white"
                                                                >
                                                                    {company.company_name}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </Tooltip.Content>
                                                </Tooltip.Portal>
                                            </Tooltip.Root>
                                        </span>
                                    )}
                                </span>
                            );
                        },
                    }),
                    columnHelper.accessor('title', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('note', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellDataOrEmpty />,
                    }),
                    columnHelper.accessor('website_url', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellLinkOrEmpty />,
                        meta: {
                            ignoreLink: true,
                        },
                    }),
                    columnHelper.accessor('facebook_url', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellLinkOrEmpty />,
                        meta: {
                            ignoreLink: true,
                        },
                    }),
                    columnHelper.accessor('linkedin_url', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellLinkOrEmpty />,
                        meta: {
                            ignoreLink: true,
                        },
                    }),
                    columnHelper.accessor('x_url', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: () => <Table.CellLinkOrEmpty />,
                        meta: {
                            ignoreLink: true,
                        },
                    }),
                    columnHelper.accessor('tags', {
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: info => {
                            const tags = info.getValue() ?? [];
                            const inlineTags = tags.slice(0, Math.min(tags.length, TAG_SLICE));
                            const hoverTags = tags.slice(Math.min(tags.length, TAG_SLICE));

                            if (inlineTags.length === 0) {
                                return <EmptyData />;
                            }

                            return (
                                <div className={'flex gap-1.5 py-2'}>
                                    <div className={'flex gap-1.5'}>
                                        {inlineTags.map(tag => (
                                            <Tag key={tag} size="sm">
                                                <FontAwesomeIcon
                                                    icon={faHashtag}
                                                    size="xs"
                                                    className="text-gray-400"
                                                />
                                                {tag}
                                            </Tag>
                                        ))}
                                    </div>
                                    {hoverTags.length > 0 && (
                                        <Tooltip.Root>
                                            <Tooltip.Trigger type={'button'}>
                                                <Badge size="sm">+ {hoverTags.length} more</Badge>
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content
                                                    onClick={(e: MouseEvent) => e.preventDefault()}
                                                    className={
                                                        'flex !max-w-[250px] flex-wrap gap-1.5'
                                                    }
                                                >
                                                    {hoverTags.map(tag => (
                                                        <Tag
                                                            key={tag}
                                                            size="sm"
                                                            className="!text-xs"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faHashtag}
                                                                size="xs"
                                                                className="!size-2 text-gray-400"
                                                            />
                                                            {tag}
                                                        </Tag>
                                                    ))}
                                                    <Tooltip.Arrow />
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    )}
                                </div>
                            );
                        },
                        meta: {
                            className: '[&>*]:h-auto !h-auto',
                        },
                    }),
                    columnHelper.display({
                        id: 'notifications',
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: info => {
                            const {
                                is_phone_subscribed,
                                is_address_subscribed,
                                is_email_subscribed,
                            } = info.row.original;

                            return (
                                <div className={'flex gap-1.5 py-2'}>
                                    <div className={'flex gap-1.5'}>
                                        {is_email_subscribed && <Tag size="sm">Email</Tag>}
                                        {is_phone_subscribed && <Tag size="sm">SMS</Tag>}
                                        {is_address_subscribed && <Tag size="sm">Mail</Tag>}
                                    </div>
                                </div>
                            );
                        },
                        meta: {
                            className: '[&>*]:h-auto !h-auto',
                        },
                    }),
                    columnHelper.display({
                        id: 'additional_emails',
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: info => {
                            const emails = info.row.original.emails.filter(
                                email => email.value !== info.row.original.primary_email,
                            );

                            if (!emails.length) {
                                return <EmptyData />;
                            }

                            const maxDisplayed = 2;
                            const displayedEmails = emails.slice(0, maxDisplayed);
                            const hiddenEmails = emails.slice(maxDisplayed);

                            return (
                                <span>
                                    {displayedEmails.map((email, index) => (
                                        <span key={email.value}>
                                            {index > 0 && ', '}
                                            {email.value}
                                        </span>
                                    ))}
                                    {hiddenEmails.length > 0 && (
                                        <span>
                                            {`, `}
                                            <Tooltip.Root>
                                                <Tooltip.Trigger>
                                                    <span>and {hiddenEmails.length} more</span>
                                                </Tooltip.Trigger>
                                                <Tooltip.Portal>
                                                    <Tooltip.Content inverse>
                                                        <Tooltip.Arrow inverse />
                                                        <div className="flex flex-col">
                                                            {hiddenEmails.map(email => (
                                                                <span key={email.value}>
                                                                    {email.value}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </Tooltip.Content>
                                                </Tooltip.Portal>
                                            </Tooltip.Root>
                                        </span>
                                    )}
                                </span>
                            );
                        },
                    }),
                    columnHelper.display({
                        id: 'additional_phones',
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: info => {
                            const phones = info.row.original.phones.filter(
                                phone => phone.value !== info.row.original.primary_phone,
                            );

                            if (!phones.length) {
                                return <EmptyData />;
                            }

                            const maxDisplayed = 2;
                            const displayedPhones = phones.slice(0, maxDisplayed);
                            const hiddenPhones = phones.slice(maxDisplayed);

                            return (
                                <span>
                                    {displayedPhones.map((phone, index) => (
                                        <span key={phone.value}>
                                            {index > 0 && ', '}
                                            {phone.value}
                                        </span>
                                    ))}
                                    {hiddenPhones.length > 0 && (
                                        <span>
                                            {`, `}
                                            <Tooltip.Root>
                                                <Tooltip.Trigger>
                                                    <span>and {hiddenPhones.length} more</span>
                                                </Tooltip.Trigger>
                                                <Tooltip.Portal>
                                                    <Tooltip.Content inverse>
                                                        <Tooltip.Arrow inverse />
                                                        <div className="flex flex-col">
                                                            {hiddenPhones.map(phone => (
                                                                <span key={phone.value}>
                                                                    {phone.value}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </Tooltip.Content>
                                                </Tooltip.Portal>
                                            </Tooltip.Root>
                                        </span>
                                    )}
                                </span>
                            );
                        },
                    }),
                    columnHelper.display({
                        id: 'additional_addresses',
                        header: curriedTransformTableKeyToReadable,
                        enableSorting: false,
                        cell: info => {
                            const addresses = info.row.original.addresses.filter(
                                address => address.id !== info.row.original.primary_address?.id,
                            );

                            if (!addresses.length) {
                                return <EmptyData />;
                            }

                            const maxDisplayed = 2;
                            const displayedAddresses = addresses.slice(0, maxDisplayed);
                            const hiddenAddresses = addresses.slice(maxDisplayed);

                            const formatAddress = (address: Address) => {
                                return [
                                    address.address_1,
                                    address.address_2,
                                    address.city,
                                    address.state,
                                    address.country,
                                ]
                                    .filter(Boolean)
                                    .join(', ');
                            };

                            return (
                                <span>
                                    {displayedAddresses.map((address, index) => (
                                        <span key={address.id}>
                                            {index > 0 && ', '}
                                            {formatAddress(address)}
                                        </span>
                                    ))}
                                    {hiddenAddresses.length > 0 && (
                                        <span>
                                            {`, `}
                                            <Tooltip.Root>
                                                <Tooltip.Trigger>
                                                    <span>and {hiddenAddresses.length} more</span>
                                                </Tooltip.Trigger>
                                                <Tooltip.Portal>
                                                    <Tooltip.Content inverse>
                                                        <Tooltip.Arrow inverse />
                                                        <div className="flex flex-col">
                                                            {hiddenAddresses.map(address => (
                                                                <span key={address.id}>
                                                                    {formatAddress(address)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </Tooltip.Content>
                                                </Tooltip.Portal>
                                            </Tooltip.Root>
                                        </span>
                                    )}
                                </span>
                            );
                        },
                    }),
                    ...(transactionSoftCredits
                        ? [
                              columnHelper.accessor('stats', {
                                  id: 'total_soft_credits',
                                  header: curriedTransformTableKeyToReadable,
                                  cell: info => {
                                      const softCredits = info.getValue().soft_credits ?? 0;
                                      return (
                                          <>
                                              <CurrencySymbol />
                                              {maskDecimal(softCredits)}
                                          </>
                                      );
                                  },
                                  meta: {
                                      sortType: 'numeric',
                                  },
                              }),
                          ]
                        : []),
                ].concat(
                    contactCustomFields.map(field =>
                        configureCustomFieldColumn(field, columnHelper),
                    ),
                )
            }
            options={{
                enableHiding: true,
                enableSorting: true,
                enablePinning: true,
                enableExpanding: true,
                meta: {
                    enableColumnOrder: true,
                },
            }}
        >
            <ContactsViewContext.Provider value={ctx}>
                <ContactsTableProvider>
                    <OveragesWarning />
                    <Content>
                        <Contacts />
                    </Content>
                </ContactsTableProvider>
            </ContactsViewContext.Provider>
        </TableCoreResourceProvider>
    );
};

export { ContactsWrapper as Contacts };
